import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Input,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Progress,
  Row,
  Col,
  Typography,
  Empty,
  Spin,
  Tooltip,
  Popconfirm,
  Alert,
  Image
} from 'antd';
import { useAuth } from '../../contexts/AuthContext';
// @ts-ignore
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  DownloadOutlined,
  PlayCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  ReloadOutlined,
  KeyOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  CheckOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface ModItem {
  id: string;
  name: string;
  description: string;
  image_url?: string;
  file_size?: number | null;
  subscriptions?: number | null;
  favorited?: number | null;
  views?: number | null;
  created?: number | null;
  updated?: number | null;
  tags?: string[] | string;
  creator?: string;
  creator_steam_id?: string;
  source?: string;
  // 数据库字段
  mod_id?: string;
  mod_name?: string;
  mod_description?: string;
  mod_image_url?: string;
  is_downloaded?: boolean;
  is_active?: boolean;
  mod_order?: number;
  download_progress?: number;
  downloaded_at?: string;
  activated_at?: string;
}

interface ModManagementProps {
  serverId: string;
  gameType: string;
}

const ModManagement: React.FC<ModManagementProps> = ({ serverId, gameType }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('installed');
  const [installedMods, setInstalledMods] = useState<ModItem[]>([]);
  const [downloadingMods, setDownloadingMods] = useState<ModItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modSupportEnabled, setModSupportEnabled] = useState(false);
  const [activationModalVisible, setActivationModalVisible] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  
  // 手动添加MOD相关状态
  const [modIdInput, setModIdInput] = useState('');
  const [modIdList, setModIdList] = useState<string[]>([]);
  const [previewMods, setPreviewMods] = useState<ModItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);
  const [modToggleLoading, setModToggleLoading] = useState<Set<string>>(new Set());
  const [modDeleteLoading, setModDeleteLoading] = useState<Set<string>>(new Set());
  const [modInstallLoading, setModInstallLoading] = useState<Set<string>>(new Set());
  const [modInfoLoading, setModInfoLoading] = useState<Set<string>>(new Set());
  const [modInfoTimeouts, setModInfoTimeouts] = useState<Map<string, NodeJS.Timeout>>(new Map());
  const [installingModId, setInstallingModId] = useState<string | null>(null); // 当前正在安装的MOD ID
  const [stepsCardCollapsed, setStepsCardCollapsed] = useState(() => {
    // 从localStorage读取折叠状态，默认为false（展开）
    const saved = localStorage.getItem('modStepsCardCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [dragSortAlertVisible, setDragSortAlertVisible] = useState(() => {
    // 从localStorage读取拖拽排序提示框显示状态，默认为true（显示）
    const saved = localStorage.getItem('modDragSortAlertVisible');
    return saved ? JSON.parse(saved) : true;
  });

  // 保存折叠状态到localStorage
  useEffect(() => {
    localStorage.setItem('modStepsCardCollapsed', JSON.stringify(stepsCardCollapsed));
  }, [stepsCardCollapsed]);

  // 保存拖拽排序提示框状态到localStorage
  useEffect(() => {
    localStorage.setItem('modDragSortAlertVisible', JSON.stringify(dragSortAlertVisible));
  }, [dragSortAlertVisible]);

  // 初始化检查MOD支持状态
  useEffect(() => {
    checkModSupport();
    if (modSupportEnabled) {
      loadInstalledMods();
      startProgressPolling();
    }
  }, [serverId, modSupportEnabled]);

  // 组件卸载时清理轮询和超时
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      // 清理所有MOD信息获取超时
      modInfoTimeouts.forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [progressInterval, modInfoTimeouts]);

  // 检查MOD支持状态
  const checkModSupport = async () => {
    try {
      // 先检查服务器是否启用了MOD支持
      const serverResponse = await api.get(`/servers/${serverId}`);
      const serverModSupport = serverResponse.data.mod_support_enabled;
      
      console.log('服务器MOD支持状态:', serverModSupport);
      
      if (!serverModSupport) {
        setModSupportEnabled(false);
        return;
      }
      
      // 如果服务器启用了MOD支持，再获取MOD列表
      try {
        const response = await api.get(`/mods/server/${serverId}`);
        setModSupportEnabled(true);
        setInstalledMods(response.data.mods || []);
      } catch (modError: any) {
        console.error('获取MOD列表失败:', modError);
        // 即使获取MOD列表失败，如果服务器已启用MOD支持，仍然显示MOD管理界面
        setModSupportEnabled(true);
        setInstalledMods([]);
        if (modError.response?.status === 500) {
          message.warning('MOD列表加载失败，但MOD功能已启用');
        }
      }
    } catch (error: any) {
      console.error('检查MOD支持状态失败:', error);
      setModSupportEnabled(false);
      if (error.response?.status !== 500) {
        message.error('检查MOD支持状态失败');
      }
    }
  };

  // 加载已安装的MOD
  const loadInstalledMods = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/mods/server/${serverId}`);
      setInstalledMods(response.data.mods || []);
    } catch (error: any) {
      message.error('加载已安装MOD失败');
    } finally {
      setLoading(false);
    }
  };


  // 下载进度轮询
  const startProgressPolling = useCallback(() => {
    // 如果已经有轮询在运行，先停止
    if (progressInterval) {
      clearInterval(progressInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/mods/server/${serverId}/progress`);
        setDownloadingMods(response.data.downloadingMods || []);
        
        // 如果没有正在下载的MOD，停止轮询
        if (!response.data.downloadingMods || response.data.downloadingMods.length === 0) {
          clearInterval(interval);
          setProgressInterval(null);
          // 刷新已安装MOD列表
          loadInstalledMods();
        }
      } catch (error) {
        console.error('获取下载进度失败:', error);
      }
    }, 2000);

    setProgressInterval(interval);
    return () => clearInterval(interval);
  }, [serverId, loadInstalledMods]);

  // 停止进度轮询
  const stopProgressPolling = useCallback(() => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
      console.log('已停止MOD下载进度轮询');
    }
  }, [progressInterval]);

  // 安装MOD
  const installMod = async (mod: ModItem) => {
    const modId = mod.id;
    
    // 检查是否已有MOD正在安装
    if (installingModId) {
      message.warning('请等待当前MOD安装完成后再安装其他MOD');
      return;
    }
    
    try {
      // 设置当前安装的MOD ID
      setInstallingModId(modId);
      setModInstallLoading(prev => new Set(prev).add(modId));
      message.loading('正在开始安装MOD...', 0);
      
      const installData = {
        modId: mod.id,
        modName: mod.name,
        modDescription: mod.description,
        modImageUrl: mod.image_url,
        fileSize: mod.file_size,
        subscriptions: mod.subscriptions,
        favorited: mod.favorited,
        views: mod.views,
        created: mod.created,
        updated: mod.updated,
        creator: mod.creator,
        creator_steam_id: mod.creator_steam_id,
        tags: mod.tags,
        source: mod.source
      };
      
      console.log(`[MOD安装] 前端发送数据:`, JSON.stringify(installData, null, 2));
      
      const response = await api.post(`/mods/server/${serverId}/install`, installData);

      message.destroy(); // 清除loading消息
      
      // 检查是否跳过了下载
      if (response.data.skipped) {
        message.success(`MOD "${mod.name}" 已存在，跳过下载`);
      } else {
        message.success(`MOD "${mod.name}" 安装已开始`);
      }
      
      // 开始轮询下载进度
      startProgressPolling();
      
      // 如果是从预览列表安装的，从预览列表中移除
      if (previewMods.some(m => m.id === modId)) {
        setPreviewMods(prev => prev.filter(m => m.id !== modId));
        setModIdList(prev => prev.filter(id => id !== modId));
      }
      
      // 切换到已安装标签页
      setActiveTab('installed');
    } catch (error: any) {
      message.destroy(); // 清除loading消息
      message.error('安装MOD失败: ' + (error.response?.data?.message || error.message));
    } finally {
      // 清除当前安装的MOD ID和loading状态
      setInstallingModId(null);
      setModInstallLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(modId);
        return newSet;
      });
    }
  };

  // 激活/停用MOD
  const toggleMod = async (modRecordId: string | number, active: boolean) => {
    const modId = modRecordId.toString();
    
    try {
      console.log('尝试切换MOD状态，ID:', modRecordId, '类型:', typeof modRecordId);
      
      // 立即显示loading状态和反馈
      setModToggleLoading(prev => new Set(prev).add(modId));
      message.loading(active ? '正在激活MOD...' : '正在停用MOD...', 0);
      
      // 确保modRecordId是有效的数字
      let id: number;
      
      if (typeof modRecordId === 'string') {
        // 清理字符串，移除空格和无效字符
        const cleanId = modRecordId.toString().trim();
        if (!cleanId || cleanId === '' || cleanId.includes(' ')) {
          message.error('无效的MOD ID: ' + modRecordId);
          setModToggleLoading(prev => {
            const newSet = new Set(prev);
            newSet.delete(modId);
            return newSet;
          });
          return;
        }
        id = parseInt(cleanId, 10);
      } else {
        id = modRecordId;
      }
      
      if (isNaN(id) || id <= 0) {
        message.error('无效的MOD ID: ' + modRecordId);
        setModToggleLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(modId);
          return newSet;
        });
        return;
      }
      
      console.log('切换MOD状态，清理后的ID:', id);
      await api.post(`/mods/server/${serverId}/toggle/${id}`, {
        active
      });

      // 成功后立即更新本地状态，提供即时反馈
      setInstalledMods(prevMods => 
        prevMods.map(mod => 
          mod.id === modRecordId 
            ? { ...mod, is_active: active }
            : mod
        )
      );

      message.destroy(); // 清除loading消息
      message.success(active ? 'MOD已激活' : 'MOD已停用');
      
      // 异步刷新完整列表以确保数据同步
      setTimeout(() => {
      loadInstalledMods();
      }, 500);
      
    } catch (error: any) {
      console.error('切换MOD状态失败:', error);
      message.destroy(); // 清除loading消息
      message.error('切换MOD状态失败: ' + (error.response?.data?.message || error.message));
    } finally {
      // 清除loading状态
      setModToggleLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(modId);
        return newSet;
      });
    }
  };

  // 删除MOD
  const deleteMod = async (modRecordId: string | number) => {
    const modId = modRecordId.toString();
    
    try {
      console.log('尝试删除MOD，ID:', modRecordId, '类型:', typeof modRecordId);
      
      // 立即显示loading状态和反馈
      setModDeleteLoading(prev => new Set(prev).add(modId));
      message.loading('正在删除MOD...', 0);
      
      // 确保modRecordId是有效的数字
      let id: number;
      
      if (typeof modRecordId === 'string') {
        // 清理字符串，移除空格和无效字符
        const cleanId = modRecordId.toString().trim();
        if (!cleanId || cleanId === '' || cleanId.includes(' ')) {
          message.error('无效的MOD ID: ' + modRecordId);
          setModDeleteLoading(prev => {
            const newSet = new Set(prev);
            newSet.delete(modId);
            return newSet;
          });
          return;
        }
        id = parseInt(cleanId, 10);
      } else {
        id = modRecordId;
      }
      
      if (isNaN(id) || id <= 0) {
        message.error('无效的MOD ID: ' + modRecordId);
        setModDeleteLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(modId);
          return newSet;
        });
        return;
      }
      
      // 查找MOD信息，检查是否已激活
      const modToDelete = installedMods.find(mod => mod.id === modRecordId);
      if (modToDelete && modToDelete.is_active) {
        console.log('MOD已激活，先停用MOD:', modToDelete.mod_name);
        message.destroy(); // 清除删除loading消息
        message.loading('MOD已激活，正在停用...', 0);
        
        try {
          // 先停用MOD
          await api.post(`/mods/server/${serverId}/toggle/${id}`);
          console.log('MOD停用成功，继续删除');
          
          // 更新本地状态
          setInstalledMods(prevMods => 
            prevMods.map(mod => 
              mod.id === modRecordId 
                ? { ...mod, is_active: false }
                : mod
            )
          );
          
          message.destroy(); // 清除停用loading消息
          message.loading('MOD已停用，正在删除...', 0);
        } catch (toggleError: any) {
          console.error('停用MOD失败:', toggleError);
          message.destroy(); // 清除loading消息
          message.error('停用MOD失败: ' + (toggleError.response?.data?.message || toggleError.message));
          return;
        }
      }
      
      console.log('删除MOD，清理后的ID:', id);
      await api.delete(`/mods/server/${serverId}/mod/${id}`);
      
      // 成功后立即从本地状态中移除，提供即时反馈
      setInstalledMods(prevMods => 
        prevMods.filter(mod => mod.id !== modRecordId)
      );
      
      message.destroy(); // 清除loading消息
      message.success('MOD已删除');
      
      // 停止进度轮询，因为可能没有正在下载的MOD了
      stopProgressPolling();
      
      // 异步刷新完整列表以确保数据同步
      setTimeout(() => {
        loadInstalledMods();
      }, 500);
      
    } catch (error: any) {
      console.error('删除MOD失败:', error);
      message.destroy(); // 清除loading消息
      message.error('删除MOD失败: ' + (error.response?.data?.message || error.message));
    } finally {
      // 清除loading状态
      setModDeleteLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(modId);
        return newSet;
      });
    }
  };

  // 激活MOD功能
  const activateModSupport = async () => {
    if (!activationCode.trim()) {
      message.error('请输入激活码');
      return;
    }

    try {
      await api.post('/mods/activate', {
        serverId,
        activationCode: activationCode.trim()
      });

      message.success('MOD功能已成功启用');
      setActivationModalVisible(false);
      setActivationCode('');
      setModSupportEnabled(true);
      loadInstalledMods();
    } catch (error: any) {
      message.error('激活失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (!bytes) return '未知';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // 格式化来源显示
  const formatSourceDisplay = (source: string): string => {
    switch (source) {
      case 'steam_web_api':
        return 'Steam创意工坊';
      case 'steam_workshop':
        return 'Steam Workshop';
      case 'partial_fallback':
        return 'Steam创意工坊';
      case 'fallback':
        return '基本信息';
      default:
        return '未知';
    }
  };

  // 检查MOD是否已安装
  const isModInstalled = (modId: string) => {
    return installedMods.some(mod => mod.mod_id === modId);
  };

  // 检查MOD ID是否有效
  const isValidModId = (id: any): boolean => {
    if (typeof id === 'string') {
      const cleanId = id.trim();
      return cleanId !== '' && !cleanId.includes(' ') && !isNaN(parseInt(cleanId, 10));
    }
    return typeof id === 'number' && !isNaN(id) && id > 0;
  };

  // 测试Steam API
  const testSteamApi = async () => {
    try {
      message.loading('正在测试Steam API...', 0);
      const response = await api.get('/mods/test-steam-api');
      message.destroy();
      
      if (response.data.success) {
        if (response.data.hasKey) {
          message.success(`Steam API测试成功！密钥: ${response.data.keyPreview}`);
          if (response.data.testResult) {
            message.info(`测试MOD: ${response.data.testResult.name} (来源: ${response.data.testResult.source})`);
          }
        } else {
          message.warning('Steam API密钥未配置');
        }
      } else {
        message.error(`Steam API测试失败: ${response.data.message}`);
      }
    } catch (error: any) {
      message.destroy();
      message.error(`Steam API测试失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 添加MOD ID到列表并自动获取信息
  const addModId = async () => {
    const modId = modIdInput.trim();
    if (!modId) {
      message.warning('请输入MOD ID');
      return;
    }
    
    if (!/^\d+$/.test(modId)) {
      message.error('MOD ID必须是数字');
      return;
    }
    
    if (modIdList.includes(modId)) {
      message.warning('MOD ID已存在');
      return;
    }
    
    // 添加到列表
    setModIdList(prev => [...prev, modId]);
    setModIdInput('');
    
    // 立即显示loading状态
    setModInfoLoading(prev => new Set(prev).add(modId));
    message.loading(`正在获取MOD ${modId} 的详细信息...（最多等待15秒）`, 0);
    
    // 创建临时卡片，显示基本信息
    const tempMod: ModItem = {
      id: modId,
      name: `MOD ${modId}`,
      description: '正在获取详细信息...',
      image_url: undefined,
      file_size: undefined,
      subscriptions: undefined,
      favorited: undefined,
      views: undefined,
      created: undefined,
      updated: undefined,
      tags: [],
      creator: '未知',
      source: 'loading'
    };
    
    setPreviewMods(prev => [...prev, tempMod]);
    
    // 设置8秒超时
    const timeout = setTimeout(() => {
      handleModInfoTimeout(modId);
    }, 8000);
    
    setModInfoTimeouts(prev => new Map(prev).set(modId, timeout));
    
    // 尝试获取真实MOD信息
    try {
      const response = await api.post('/mods/preview', {
        modIds: [modId]
      });
      
      const newMods = response.data.mods || [];
      if (newMods.length > 0) {
        const realMod = newMods[0];
        
        console.log(`[MOD获取] 成功获取MOD信息: ${realMod.name}, 立即更新UI`);
        console.log(`[MOD获取] realMod数据:`, JSON.stringify(realMod, null, 2));
        
        // 清除超时定时器
        const currentTimeout = modInfoTimeouts.get(modId);
        if (currentTimeout) {
          clearTimeout(currentTimeout);
          setModInfoTimeouts(prev => {
            const newMap = new Map(prev);
            newMap.delete(modId);
            return newMap;
          });
        }
        
        // 立即清除loading状态
        setModInfoLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(modId);
          return newSet;
        });
        
        // 立即更新为真实信息
        setPreviewMods(prev => 
          prev.map(mod => 
            mod.id === modId 
              ? {
                  ...mod,
                  // 使用获取到的真实数据，确保所有字段都被正确设置
                  name: realMod.name || `MOD ${modId}`,
                  description: realMod.description || '无法获取详细信息',
                  image_url: realMod.image_url || `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/items/346110/${modId}.jpg`,
                  file_size: realMod.file_size || 0,
                  subscriptions: realMod.subscriptions || 0,
                  favorited: realMod.favorited || 0,
                  views: realMod.views || 0,
                  created: realMod.created || Math.floor(Date.now() / 1000),
                  updated: realMod.updated || Math.floor(Date.now() / 1000),
                  tags: realMod.tags && realMod.tags.length > 0 ? realMod.tags : ['ARK'],
                  creator: realMod.creator || '未知',
                  creator_steam_id: realMod.creator_steam_id || undefined,
                  source: realMod.source === 'steam_web_api' ? 'steam_workshop' : (realMod.source || 'steam_workshop')
                }
              : mod
          )
        );
        
        message.destroy(); // 清除loading消息
        message.success(`成功获取MOD信息: ${realMod.name || modId} (来源: ${formatSourceDisplay(realMod.source || '未知')})`);
        
        // 自动折叠详细查找步骤卡片
        setStepsCardCollapsed(true);
        
        // 成功获取信息后立即返回，停止后续处理
        return;
      } else {
        console.log(`[MOD获取] 未获取到MOD信息，触发超时处理`);
        handleModInfoTimeout(modId);
      }
    } catch (error: any) {
      console.error('获取MOD信息失败:', error);
      handleModInfoTimeout(modId);
    }
    
    // 如果到达这里，说明没有成功获取到信息，等待超时处理
  };
  
  // 处理MOD信息获取超时
  const handleModInfoTimeout = (modId: string) => {
    // 清除超时
    const currentTimeout = modInfoTimeouts.get(modId);
    if (currentTimeout) {
      clearTimeout(currentTimeout);
      setModInfoTimeouts(prev => {
        const newMap = new Map(prev);
        newMap.delete(modId);
        return newMap;
      });
    }
    
    // 清除loading状态，允许按钮点击
    setModInfoLoading(prev => {
      const newSet = new Set(prev);
      newSet.delete(modId);
      return newSet;
    });
    
    // 更新为卡片形式，保留已获取的数据，只补充缺失的信息
    setPreviewMods(prev => 
      prev.map(mod => 
        mod.id === modId 
          ? {
              ...mod,
              // 保留已获取的数据，只补充缺失的字段
              name: mod.name && mod.name !== `MOD ${modId}` ? mod.name : `MOD ${modId}`,
              description: mod.description && mod.description !== '正在获取详细信息...' ? mod.description : '无法获取详细信息，将使用基本信息',
              image_url: mod.image_url || `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/items/346110/${modId}.jpg`,
              file_size: mod.file_size || 0,
              subscriptions: mod.subscriptions || 0,
              favorited: mod.favorited || 0,
              views: mod.views || 0,
              created: mod.created || Math.floor(Date.now() / 1000),
              updated: mod.updated || Math.floor(Date.now() / 1000),
              tags: mod.tags && mod.tags.length > 0 ? mod.tags : ['ARK'],
              creator: mod.creator && mod.creator !== '未知' ? mod.creator : '未知',
              creator_steam_id: mod.creator_steam_id || undefined,
              source: 'partial_fallback' // 标记为部分回退
            }
          : mod
      )
    );
    
    message.destroy(); // 清除loading消息
        message.success(`MOD ${modId} 信息获取超时（15秒），已保留Steam创意工坊`);
  };

  // 移除MOD ID
  const removeModId = (modId: string) => {
    // 清除超时
    const currentTimeout = modInfoTimeouts.get(modId);
    if (currentTimeout) {
      clearTimeout(currentTimeout);
      setModInfoTimeouts(prev => {
        const newMap = new Map(prev);
        newMap.delete(modId);
        return newMap;
      });
    }
    
    // 清除loading状态
    setModInfoLoading(prev => {
      const newSet = new Set(prev);
      newSet.delete(modId);
      return newSet;
    });
    
    setModIdList(prev => prev.filter(id => id !== modId));
    // 同时从预览列表中移除对应的MOD
    setPreviewMods(prev => prev.filter(mod => mod.id !== modId));
  };

  // 获取MOD预览信息
  const fetchModPreview = async (modIds: string[]) => {
    try {
      setPreviewLoading(true);
      const response = await api.post('/mods/preview', {
        modIds: modIds
      });
      
      setPreviewMods(response.data.mods || []);
    } catch (error: any) {
      message.error('获取MOD信息失败: ' + (error.response?.data?.message || error.message));
      setPreviewMods([]);
    } finally {
      setPreviewLoading(false);
    }
  };


  // 处理拖拽排序
  const handleDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;
    
    if (source.index === destination.index) {
      return;
    }

    // 更新本地状态
    const newInstalledMods = Array.from(installedMods);
    const [reorderedItem] = newInstalledMods.splice(source.index, 1);
    newInstalledMods.splice(destination.index, 0, reorderedItem);

    setInstalledMods(newInstalledMods);

    // 更新服务器端排序
    try {
      const modOrders = newInstalledMods.map((mod, index) => ({
        id: mod.id,
        order: index + 1
      }));

      await api.put(`/mods/server/${serverId}/sort`, {
        modOrders
      });

      message.success('MOD排序已更新');
    } catch (error: any) {
      message.error('更新MOD排序失败: ' + (error.response?.data?.message || error.message));
      // 恢复原状态
      loadInstalledMods();
    }
  };

  // 渲染MOD卡片
  const renderModCard = (mod: ModItem, type: 'search' | 'installed', index?: number) => {
    const isInstalled = type === 'installed';
    const modId = isInstalled ? mod.mod_id : mod.id;
    const modName = isInstalled ? mod.mod_name : mod.name;
    const modDescription = isInstalled ? mod.mod_description : mod.description;
    const modImageUrl = isInstalled ? mod.mod_image_url : mod.image_url;
    
    // 处理tags字段（可能是JSON字符串）
    let modTags = mod.tags;
    if (typeof modTags === 'string') {
      try {
        modTags = JSON.parse(modTags);
      } catch (e) {
        modTags = [];
      }
    }

    return (
      <Card
        key={isInstalled ? `installed-${mod.id}` : `search-${mod.id}`}
        size="small"
        style={{ 
          marginBottom: 16, 
          position: 'relative'
        }}
        cover={
          modImageUrl && (
            <div style={{ height: 200, overflow: 'hidden', position: 'relative' }}>
              {/* 编号显示 */}
              {isInstalled && typeof index === 'number' && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  zIndex: 10
                }}>
                  #{index + 1}
                </div>
              )}
              <Image
                src={modImageUrl}
                alt={modName}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }}
                preview={false}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgZmlsbD0iIzRBOTBFMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U3RydWN0dXJlcyBQbHVzPC90ZXh0Pjwvc3ZnPg=="
              />
              
              {/* 下载中遮罩 - 图片中间 */}
              {isInstalled && !mod.is_downloaded && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  zIndex: 20
                }}>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                  }}>
                    📥
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                  }}>
                    下载中...
                  </div>
                </div>
              )}

              {/* 状态标签 - 图片右下角 */}
              {isInstalled && (
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  zIndex: 10
                }}>
                  <Tag 
                    color={mod.is_active ? "blue" : "default"} 
                    style={{ 
                      borderRadius: '16px', 
                      fontSize: '11px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      border: 'none',
                      padding: '4px 8px'
                    }}
                  >
                    {mod.is_active ? '已激活' : '未激活'}
                  </Tag>
                </div>
              )}
            </div>
          )
        }
        actions={
          isInstalled ? [
            <Tooltip title={mod.is_active ? "停用MOD" : "激活MOD"}>
              <Button
                type={mod.is_active ? "primary" : "default"}
                icon={mod.is_active ? <StopOutlined /> : <PlayCircleOutlined />}
                onClick={() => toggleMod(mod.id, !mod.is_active)}
                disabled={!mod.is_downloaded || modToggleLoading.has(mod.id.toString())}
                loading={modToggleLoading.has(mod.id.toString())}
              />
            </Tooltip>,
            <Tooltip title={!mod.is_downloaded ? "下载中的MOD不能删除" : "删除MOD"}>
              <Popconfirm
                title="确定要删除这个MOD吗？"
                onConfirm={() => deleteMod(mod.id)}
                okText="确定"
                cancelText="取消"
                disabled={modDeleteLoading.has(mod.id.toString()) || !mod.is_downloaded}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={modDeleteLoading.has(mod.id.toString())}
                  disabled={modDeleteLoading.has(mod.id.toString()) || !mod.is_downloaded}
                />
              </Popconfirm>
            </Tooltip>
          ] : [
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => installMod(mod)}
              disabled={isModInstalled(mod.id) || modInstallLoading.has(mod.id)}
              loading={modInstallLoading.has(mod.id)}
            >
              {isModInstalled(mod.id) ? '已安装' : '安装'}
            </Button>
          ]
        }
      >
        <div style={{ padding: '8px 0' }}>
          {/* 基本信息 */}
          <div style={{ marginBottom: '12px' }}>
            <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                {modName}
              </Text>
          </div>
          
          {/* 描述 */}
              <Paragraph
                ellipsis={{ rows: 2, tooltip: true }}
            style={{ 
              marginBottom: '12px',
              color: '#8c8c8c',
              fontSize: '13px',
              lineHeight: '1.5'
            }}
              >
                {modDescription}
              </Paragraph>
              
          {/* 标签信息 */}
          <Space size="small" wrap style={{ marginBottom: '8px' }}>
            <Tag color="blue" style={{ borderRadius: '12px', fontSize: '11px' }}>
              ID: {modId}
            </Tag>
              {!isInstalled && (
              <Tag color="green" style={{ borderRadius: '12px', fontSize: '11px' }}>
                Steam Workshop
              </Tag>
            )}
            {modTags && modTags.length > 0 && (
              <Tag color="purple" style={{ borderRadius: '12px', fontSize: '11px' }}>
                {modTags[0]}
              </Tag>
                  )}
                </Space>
          
          {/* 详细统计信息 */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: '8px',
            padding: '8px 0',
            borderTop: '1px solid #f0f0f0',
            marginTop: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>订阅数</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                {formatNumber((isInstalled ? mod.subscriptions : mod.subscriptions) || 0)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>收藏数</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                {formatNumber((isInstalled ? mod.favorited : mod.favorited) || 0)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>文件大小</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                {formatFileSize((isInstalled ? mod.file_size : mod.file_size) || 0)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>浏览数</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                {formatNumber((isInstalled ? mod.views : mod.views) || 0)}
              </div>
            </div>
          </div>
          
          {/* 额外信息 - 创建时间、更新时间、作者 */}
          <div style={{ 
            padding: '8px 0',
            borderTop: '1px solid #f0f0f0',
            marginTop: '8px'
          }}>
            {/* 时间信息 */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '8px',
              fontSize: '12px',
              color: '#8c8c8c'
            }}>
              <div>
                <span style={{ fontWeight: 'bold' }}>创建:</span> {(isInstalled ? mod.created : mod.created) ? new Date(((isInstalled ? mod.created : mod.created) || 0) * 1000).toLocaleDateString('zh-CN') : '未知'}
              </div>
              <div>
                <span style={{ fontWeight: 'bold' }}>更新:</span> {(isInstalled ? mod.updated : mod.updated) ? new Date(((isInstalled ? mod.updated : mod.updated) || 0) * 1000).toLocaleDateString('zh-CN') : '未知'}
              </div>
            </div>
            
            {/* 作者信息和状态 */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              fontSize: '12px',
              color: '#8c8c8c'
            }}>
              <div>
                <span style={{ fontWeight: 'bold' }}>作者:</span> {(isInstalled ? mod.creator : mod.creator) || '未知'}
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>状态</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: isInstalled ? (mod.is_active ? '#52c41a' : (mod.is_downloaded ? '#1890ff' : '#faad14')) : '#1890ff' }}>
                  {isInstalled ? (mod.is_active ? '已激活' : (mod.is_downloaded ? '已安装' : '下载中')) : '待安装'}
                </div>
              </div>
            </div>
          </div>
          
            </div>
      </Card>
    );
  };

  // 如果MOD功能未启用，显示激活界面
  if (!modSupportEnabled) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <Title level={3}>MOD功能未启用</Title>
          <Paragraph>
            您的服务器尚未开启MOD支持功能。<br />
            请使用激活码启用MOD功能，或在创建新服务器时选择MOD支持选项。
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<KeyOutlined />}
            onClick={() => setActivationModalVisible(true)}
          >
            使用激活码启用
          </Button>
        </div>

        <Modal
          title="启用MOD功能"
          open={activationModalVisible}
          onOk={activateModSupport}
          onCancel={() => {
            setActivationModalVisible(false);
            setActivationCode('');
          }}
          okText="激活"
          cancelText="取消"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>请输入MOD功能激活码：</Text>
            <Input
              placeholder="请输入激活码"
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value)}
              onPressEnter={activateModSupport}
            />
            <Alert
              message="注意"
              description="激活码一旦使用将无法撤销，请确认服务器信息正确。"
              type="info"
              showIcon
            />
          </Space>
        </Modal>
      </Card>
    );
  }

  const tabItems = [
    {
      key: 'installed',
      label: `已安装MOD (${installedMods.length})`,
      children: (
        <div>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadInstalledMods}
                loading={loading}
              >
                刷新
              </Button>
              <Text type="secondary">
                激活的MOD将自动写入服务器配置文件
              </Text>
            </Space>
            
            {dragSortAlertVisible && (
            <Alert
              message="MOD支持拖拽排序"
              description="您可以通过拖拽MOD卡片来调整加载顺序，编号越小越先加载。激活的MOD将按编号顺序启用，如需排序请注意。"
              type="info"
              showIcon
                closable
                onClose={() => setDragSortAlertVisible(false)}
              style={{ marginBottom: 16 }}
            />
            )}
            
            {downloadingMods.length > 0 && (
              <Alert
                message={`正在下载 ${downloadingMods.length} 个MOD`}
                description={
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {downloadingMods.map(mod => (
                      <div key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>{mod.mod_name}</Text>
                        <Tag color="processing" style={{ margin: 0 }}>
                          📥 下载中...
                        </Tag>
                      </div>
                    ))}
                  </Space>
                }
                type="info"
                showIcon
              />
            )}
          </Space>

          <Spin spinning={loading}>
            {installedMods.length === 0 ? (
              <Empty
                description="暂无已安装的MOD"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={() => setActiveTab('manual')}>
                  现在添加MOD
                </Button>
              </Empty>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="mods-list" direction="horizontal">
                  {(provided: any, snapshot: any) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        background: snapshot.isDraggingOver ? '#f0f0f0' : 'transparent',
                        borderRadius: '8px',
                        padding: '8px',
                        minHeight: '200px'
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                        {installedMods
                          .filter(mod => isValidModId(mod.id))
                          .map((mod, index) => (
                            <div key={mod.id} style={{ width: '300px', flexShrink: 0 }}>
                              <Draggable draggableId={mod.id.toString()} index={index}>
                                {(provided: any, snapshot: any) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="mod-drag-item"
                                    style={{
                                      // 完全使用react-beautiful-dnd的默认样式
                                      ...provided.draggableProps.style,
                                    }}
                                  >
                                    {renderModCard(mod, 'installed', index)}
                                  </div>
                                )}
                              </Draggable>
                            </div>
                          ))}
                      </div>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Spin>
        </div>
      )
    },
    {
      key: 'manual',
      label: '添加MOD',
      children: (
        <div>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Card 
              size="small" 
              title={
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => setStepsCardCollapsed(!stepsCardCollapsed)}
                >
                  <span>📖 详细查找步骤</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {stepsCardCollapsed ? '点击展开' : '点击折叠'}
                  </span>
                </div>
              }
              style={{ marginBottom: 16 }}
            >
              {!stepsCardCollapsed && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <Text strong>步骤1：</Text> 打开 <a href="https://steamcommunity.com/app/346110/workshop/" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>ARK创意工坊</a>
                    <Text strong>       |</Text> 无法打开请下载选择这两个软件中的一个，下载启用即可（免费软件） <a href="https://www.steamspeed.cn/" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>SteamSpeed</a>  或者 <a href="https://www.dogfight360.com/blog/18682/" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>Steamcommunity_302</a>  除非你有科学上网，打不开创意工坊读取不到MOD的信息。
                </div>
                <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <Text strong>步骤2：</Text> 在"最受欢迎"或"浏览项目"中找到您想要的MOD，点击进入
                </div>
                <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <Text strong>步骤3：</Text> 查看浏览器地址栏，找到类似这样的URL：
                  <br />
                  <code style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    padding: '4px 8px', 
                    backgroundColor: '#fff', 
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    https://steamcommunity.com/sharedfiles/filedetails/?id=<strong style={{ color: '#ff4d4f' }}>731604991</strong>
                  </code>
                </div>
                <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <Text strong>步骤4：</Text> 复制红色数字部分（如：731604991），这就是MOD ID
                </div>
              </Space>
              )}
            </Card>

            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>添加MOD ID</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="输入Steam Workshop MOD ID (例如: 731604991)"
                    value={modIdInput}
                    onChange={(e) => setModIdInput(e.target.value)}
                    onPressEnter={addModId}
                    style={{ flex: 1 }}
                    disabled={previewLoading}
                  />
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={addModId}
                    loading={previewLoading}
                    disabled={previewLoading}
                  >
                    {previewLoading ? '获取中...' : '添加'}
                  </Button>
                  {isAdmin && (
                    <Button 
                      onClick={testSteamApi}
                      style={{ marginLeft: 8 }}
                    >
                      测试Steam API
                    </Button>
                  )}
                </Space.Compact>
                
                <div style={{ marginTop: 16 }}>
                  <Text strong>🔥 热门MOD推荐：</Text>
                  <div style={{ marginTop: 8 }}>
                    <Space wrap>
                      <Tag 
                        color="blue" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setModIdInput('731604991');
                          addModId();
                        }}
                      >
                        Structures Plus (S+) - 731604991
                      </Tag>
                      <Tag 
                        color="green" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setModIdInput('889745138');
                          addModId();
                        }}
                      >
                        Awesome SpyGlass - 889745138
                      </Tag>
                      <Tag 
                        color="orange" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setModIdInput('1404697612');
                          addModId();
                        }}
                      >
                        Dino Storage v2 - 1404697612
                      </Tag>
                      <Tag 
                        color="purple" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setModIdInput('849985437');
                          addModId();
                        }}
                      >
                        Classic Flyers - 849985437
                      </Tag>
                    </Space>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: '12px' }}>
                      点击标签快速添加MOD（自动获取信息）
                    </Text>
                  </div>
                </div>

                {modIdList.length > 0 && (
                  <div>
                    <Text strong>已添加的MOD ID:</Text>
                    <div style={{ marginTop: 8 }}>
                      {modIdList.map(modId => (
                        <Tag
                          key={modId}
                          closable
                          onClose={() => removeModId(modId)}
                          style={{ marginBottom: 4 }}
                        >
                          {modId}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </Space>
            </Card>
          </Space>

          {previewMods.length > 0 && (
            <div>
              <Space style={{ marginBottom: 16 }}>
                <Text strong>MOD预览</Text>
                {installingModId && (
                  <Text type="warning">正在安装MOD，请等待完成...</Text>
                )}
              </Space>
              
              <Row gutter={[16, 16]}>
                {previewMods.map(mod => {
                  const isLoading = modInfoLoading.has(mod.id);
                  const isFallback = mod.source === 'fallback';
                  const isPartialFallback = mod.source === 'partial_fallback';
                  const isSteamWorkshop = mod.source === 'steam_workshop';
                  
                  // 调试日志
                  if (mod.id === '889745138') {
                    console.log(`[MOD渲染] MOD ${mod.id} 状态:`, {
                      isLoading,
                      source: mod.source,
                      name: mod.name,
                      modInfoLoading: Array.from(modInfoLoading)
                    });
                  }
                  
                  return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={mod.id}>
                    <Card
                      size="small"
                      style={{ 
                        marginBottom: 16,
                          border: '1px solid #d9d9d9',
                        borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                          opacity: isLoading ? 0.7 : 1
                      }}
                        hoverable={!isLoading}
                      cover={
                        mod.image_url ? (
                          <div style={{ 
                            height: 180, 
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            {/* 真实预览图 */}
                            {isLoading ? (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                padding: '20px',
                                boxSizing: 'border-box'
                              }}>
                                详细信息获取中...
                              </div>
                            ) : (
                              <>
                            <Image
                              src={mod.image_url}
                              alt={mod.name}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }}
                              preview={false}
                                  fallback={`data:image/svg+xml;base64,${btoa(`<svg width="400" height="300" xmlns="http://www.w3.org/2000/svg"><rect width="400" height="300" fill="#667eea"/><text x="50%" y="50%" font-family="Arial" font-size="24" fill="white" text-anchor="middle" dy=".3em">MOD ${mod.id}</text></svg>`)}`}
                                />
                                
                                {/* 状态标签 - 图片右下角 */}
                                {isModInstalled(mod.id) && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '8px',
                                    right: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    zIndex: 10
                                  }}>
                                    {mod.is_downloaded ? (
                                      <Tag color="green" style={{ 
                                        borderRadius: '16px', 
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        border: 'none',
                                        padding: '4px 8px'
                                      }}>已下载</Tag>
                                    ) : (
                                      <Tag color="processing" style={{ 
                                        borderRadius: '16px', 
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        border: 'none',
                                        padding: '4px 8px'
                                      }}>
                                        下载中 {mod.download_progress}%
                                      </Tag>
                                    )}
                                    <Tag 
                                      color={mod.is_active ? "blue" : "default"} 
                                      style={{ 
                                        borderRadius: '16px', 
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        border: 'none',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {mod.is_active ? '已激活' : '未激活'}
                                    </Tag>
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Loading 遮罩 */}
                            {isLoading && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.6)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: 'white'
                              }}>
                                <Spin size="large" />
                                <div style={{ marginTop: 8, fontSize: '12px' }}>
                                  正在获取详细信息...
                                </div>
                              </div>
                            )}
                            
                            {/* 游戏类型标识 */}
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'rgba(0, 0, 0, 0.7)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: 'white'
                            }}>
                              ARK
                            </div>
                            
                            {/* MOD ID 标识 */}
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              background: 'rgba(0, 0, 0, 0.7)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: 'white'
                            }}>
                              ID: {mod.id}
                            </div>
                            
                            {/* 状态标识 */}
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: isLoading ? 'rgba(255, 193, 7, 0.9)' : 
                                        isSteamWorkshop ? 'rgba(40, 167, 69, 0.9)' : 
                                        isPartialFallback ? 'rgba(255, 152, 0, 0.9)' :
                                        isFallback ? 'rgba(108, 117, 125, 0.9)' : 'rgba(0, 123, 255, 0.9)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              color: 'white'
                            }}>
                              {isLoading ? '获取中...' : 
                               isSteamWorkshop ? 'Steam Workshop' : 
                               isPartialFallback ? 'Steam创意工坊' :
                               isFallback ? '基本信息' : '未知'}
                            </div>
                            
                            {/* 装饰性元素 */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: isLoading ? 'linear-gradient(90deg, #ffc107, #ff9800)' :
                                        isSteamWorkshop ? 'linear-gradient(90deg, #28a745, #20c997)' :
                                        isPartialFallback ? 'linear-gradient(90deg, #ff9800, #ff5722)' :
                                        isFallback ? 'linear-gradient(90deg, #6c757d, #495057)' :
                                        'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)',
                              backgroundSize: '400% 100%',
                              animation: isLoading ? 'none' : 'shimmer 3s ease-in-out infinite'
                            }} />
                          </div>
                        ) : (
                          <div style={{ 
                            height: 180, 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            {/* 游戏类型标识 */}
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'rgba(255, 255, 255, 0.9)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#1890ff'
                            }}>
                              ARK
                            </div>
                            
                            {/* MOD ID 大号显示 */}
                            <div style={{
                              fontSize: '24px',
                              fontWeight: 'bold',
                              color: 'white',
                              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                              marginBottom: '8px'
                            }}>
                              {mod.id}
                            </div>
                            
                            {/* MOD 名称 */}
                            <div style={{
                              fontSize: '14px',
                              color: 'rgba(255, 255, 255, 0.9)',
                              textAlign: 'center',
                              padding: '0 16px',
                              lineHeight: '1.4'
                            }}>
                              {mod.name}
                            </div>
                            
                            {/* 装饰性元素 */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)',
                              backgroundSize: '400% 100%',
                              animation: 'shimmer 3s ease-in-out infinite'
                            }} />
                          </div>
                        )
                      }
                      actions={[
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            installMod(mod);
                          }}
                          disabled={isModInstalled(mod.id) || modInstallLoading.has(mod.id) || installingModId !== null || isLoading}
                          loading={modInstallLoading.has(mod.id)}
                          style={{
                            borderRadius: '20px',
                            height: '36px',
                            fontWeight: 'bold'
                          }}
                        >
                          {isModInstalled(mod.id) ? '已安装' : isLoading ? '获取信息中...' : '安装'}
                        </Button>
                      ]}
                    >
                      <div style={{ padding: '8px 0' }}>
                        {/* 基本信息 */}
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                            {mod.name}
                          </Text>
                        </div>
                        
                        {/* 描述 */}
                        <Paragraph
                          ellipsis={{ rows: 2, tooltip: true }}
                          style={{ 
                            marginBottom: '12px',
                            color: '#8c8c8c',
                            fontSize: '13px',
                            lineHeight: '1.5'
                          }}
                        >
                          {mod.description}
                        </Paragraph>
                        
                        {/* 标签信息 */}
                        <Space size="small" wrap style={{ marginBottom: '8px' }}>
                          <Tag color="blue" style={{ borderRadius: '12px', fontSize: '11px' }}>
                            ID: {mod.id}
                          </Tag>
                          <Tag color={isSteamWorkshop ? 'green' : isPartialFallback ? 'orange' : isFallback ? 'gray' : 'purple'} style={{ borderRadius: '12px', fontSize: '11px' }}>
                            {isSteamWorkshop ? 'Steam Workshop' : isPartialFallback ? 'Steam创意工坊' : isFallback ? '基本信息' : '未知来源'}
                          </Tag>
                          {mod.tags && mod.tags.length > 0 && (
                            <Tag color="purple" style={{ borderRadius: '12px', fontSize: '11px' }}>
                              {mod.tags[0]}
                            </Tag>
                          )}
                          {isLoading && (
                            <Tag color="processing" style={{ borderRadius: '12px', fontSize: '11px' }}>
                              获取中...
                            </Tag>
                          )}
                        </Space>
                        
                        {/* 详细统计信息 */}
                        {(isSteamWorkshop || isPartialFallback) && (
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr 1fr 1fr',
                            gap: '8px',
                            padding: '8px 0',
                            borderTop: '1px solid #f0f0f0',
                            marginTop: '8px'
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>订阅数</div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                                {formatNumber(mod.subscriptions || 0)}
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>收藏数</div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                                {formatNumber(mod.favorited || 0)}
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>文件大小</div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                                {formatFileSize(mod.file_size || 0)}
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>浏览数</div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                                {formatNumber(mod.views || 0)}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* 额外信息 - 创建时间、更新时间、标签 */}
                        {(isSteamWorkshop || isPartialFallback) && (
                          <div style={{ 
                            padding: '8px 0',
                            borderTop: '1px solid #f0f0f0',
                            marginTop: '8px'
                          }}>
                            {/* 时间信息 */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              marginBottom: '8px',
                              fontSize: '12px',
                              color: '#8c8c8c'
                            }}>
                              <div>
                                <span style={{ fontWeight: 'bold' }}>创建:</span> {mod.created ? new Date(mod.created * 1000).toLocaleDateString('zh-CN') : '未知'}
                              </div>
                              <div>
                                <span style={{ fontWeight: 'bold' }}>更新:</span> {mod.updated ? new Date(mod.updated * 1000).toLocaleDateString('zh-CN') : '未知'}
                              </div>
                            </div>
                            
                            {/* 标签信息 */}
                            {mod.tags && mod.tags.length > 0 && (
                              <div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 'bold' }}>标签:</span>
                                </div>
                                <Space size="small" wrap>
                                  {(Array.isArray(mod.tags) ? mod.tags : []).map((tag: string, index: number) => (
                                    <Tag key={index} color="blue" style={{ fontSize: '10px', marginBottom: '2px' }}>
                                      {tag}
                                    </Tag>
                                  ))}
                                </Space>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* 基本信息 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '8px 0',
                          borderTop: '1px solid #f0f0f0',
                          marginTop: '8px'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>来源</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                              {isSteamWorkshop ? 'Steam Workshop' : isPartialFallback ? 'Steam创意工坊' : isFallback ? '基本信息' : '未知'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>创建者</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#262626' }}>
                              {mod.creator && mod.creator !== 'Unknown' && !mod.creator.startsWith('765611') 
                                ? mod.creator 
                                : mod.creator && mod.creator.startsWith('765611')
                                ? `Steam用户 (${mod.creator})`
                                : (mod as any).creator_steam_id && (mod as any).creator_steam_id.startsWith('765611')
                                ? `Steam用户 (${(mod as any).creator_steam_id})`
                                : '未知'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>状态</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: isModInstalled(mod.id) ? '#52c41a' : '#1890ff' }}>
                              {isModInstalled(mod.id) ? '已安装' : '待安装'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -400% 0; }
            100% { background-position: 400% 0; }
          }
          
          /* 拖拽排序基础样式 */
          .mod-drag-item {
            cursor: grab;
          }
          
          .mod-drag-item:active {
            cursor: grabbing;
          }
        `}
      </style>
      <Card title="MOD管理" extra={
        <Space>
          <Tag color="processing">ARK: Survival Evolved</Tag>
          <Text type="secondary">服务器 #{serverId}</Text>
        </Space>
      }>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>
    </>
  );
};

export default ModManagement;